import { defineStore } from 'pinia';
import { ref } from 'vue';
import http, { csrf } from '@/api/http';

export interface AdminUser {
    id: number;
    name: string;
    email: string;
    role?: string;
    two_factor_enabled: boolean;
}

export const useAuthStore = defineStore('auth', () => {
    const user = ref<AdminUser | null>(null);
    const loading = ref(false);
    const takeoverWaiting = ref(false);

    async function fetchUser() {
        const { data } = await http.get('/api/admin/me');
        // Validate the response shape. Was the root cause of the
        // "admin enters directly but shows no data" bug 2026-07-07:
        // when CORS blocked cookies or the SPA fallback served
        // /index.html, axios received a truthy HTML string that the
        // store treated as a valid user object → router beforeEach let
        // the request through → Dashboard rendered blank. Now we
        // require the exact shape the backend returns; anything else
        // is treated as a failed lookup and the beforeEach redirects
        // to /login.
        if (!data || typeof data !== 'object' || typeof data.id !== 'number' || typeof data.email !== 'string') {
            user.value = null;
            throw new Error('Invalid /me response — expected AdminUser payload');
        }
        user.value = data as AdminUser;
    }

    async function login(email: string, password: string, code?: string) {
        loading.value = true;
        try {
            await csrf();
            const resp = await http.post('/api/admin/login', { email, password, code });

            // Single-session handoff: already signed in elsewhere. Poll until the
            // incumbent approves / the grace elapses, then complete; or reject.
            if (resp.data?.status === 'pending_takeover') {
                takeoverWaiting.value = true;
                try {
                    const outcome = await waitForTakeover(resp.data.takeover_token);
                    if (outcome === 'rejected') {
                        const err: any = new Error('takeover_rejected');
                        err.code = 'takeover_rejected';
                        throw err;
                    }
                    await http.post('/api/admin/login', { email, password, code, takeover_token: resp.data.takeover_token });
                } finally {
                    takeoverWaiting.value = false;
                }
            }

            await fetchUser();
        } finally {
            loading.value = false;
        }
    }

    async function waitForTakeover(token: string): Promise<'approved' | 'rejected' | 'expired' | 'timeout'> {
        const deadline = Date.now() + 30000;
        while (Date.now() < deadline) {
            await new Promise((r) => setTimeout(r, 1500));
            try {
                const { data } = await http.get(`/api/admin/session/takeover/${token}`);
                if (data.status === 'approved' || data.status === 'expired') return data.status;
                if (data.status === 'rejected') return 'rejected';
            } catch { /* keep polling */ }
        }
        return 'timeout';
    }

    async function logout() {
        await http.post('/api/admin/logout');
        user.value = null;
    }

    return { user, loading, takeoverWaiting, fetchUser, login, logout };
});
